import { StepBasedForm, SuccessPage } from '@components'

import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingCheckmark } from '../../../../../../assets/images'
import { TOTAL_STEPS } from './constants/totalSteps'
import { IntegrationFormView } from './elements/IntegrationFormView'
import { IntroScreen } from './elements/IntroScreen'
import { useTrackVatDeclarationsIntegrationFormEvent } from './hooks/useTrackVatDeclarationsIntegrationFormEvent'

interface VatDeclarationIntegrationFormProps {
  onBack: (showForm: boolean) => void
  onSuccess?: () => void
}

export const VatDeclarationIntegrationForm = ({ onBack, onSuccess }: VatDeclarationIntegrationFormProps) => {
  const { t } = useTranslation()
  const { trackEvent } = useTrackVatDeclarationsIntegrationFormEvent()

  const [currentStep, setCurrentStep] = useState(1)

  const handleNext = useCallback(() => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1)
      trackEvent(currentStep, 'submitButton')
    }
  }, [currentStep, trackEvent])

  const handleFinish = useCallback(() => {
    onBack(false)
    trackEvent(currentStep, 'cancelButton')
  }, [currentStep, onBack, trackEvent])

  const handleCloseForm = useCallback(() => {
    onSuccess?.()
    trackEvent(currentStep, 'submitButton')
  }, [currentStep, onSuccess, trackEvent])

  const stepLabels = [
    t('vat_declarations.integration_form.step.confirm'),
    t('vat_declarations.integration_form.step.submit'),
    t('vat_declarations.integration_form.step.submitted'),
  ]

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <IntroScreen onNext={handleNext} onBack={handleFinish} />
      case 2:
        return <IntegrationFormView onNext={handleNext} onCancel={handleFinish} />
      case 3:
        return (
          <SuccessPage
            onDoneClick={handleCloseForm}
            customIcon={<LoadingCheckmark />}
            headerLabel={t('vat_declarations.integration_form.success.title')}
            bodyLabel={t('vat_declarations.integration_form.success.body')}
            buttonLabel={t('close')}
          />
        )
      default:
        return null
    }
  }

  return (
    <StepBasedForm
      onBack={handleFinish}
      withProgressBar
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      stepLabels={stepLabels}
    >
      {renderCurrentStep()}
    </StepBasedForm>
  )
}
